import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { roleLabel } from '../../core/rbac/role-label';
import { AuthService } from '../../core/services/auth.service';
import { ToastComponent } from '../../shared/toast/toast.component';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [FormsModule, ToastComponent],
  templateUrl: './profile.component.html',
})
export class ProfileComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly officer = this.auth.officer;
  readonly message = signal<string | null>(null);
  readonly messageTone = signal<'success' | 'error'>('success');
  /** Set when the server rejects the typed current password, cleared on edit. */
  readonly currentPasswordError = signal(false);
  readonly initials = computed(() => {
    const name = this.officer()?.name ?? '';
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  });

  readonly roleLabel = computed(() => roleLabel(this.officer()?.role));

  name = '';
  email = '';
  phone = '';
  currentPassword = '';
  newPassword = '';
  readonly busy = signal(false);

  constructor() {
    // Session is the source of truth: it changes on login and on the token
    // reissued by a save, so the form must not keep one-shot initial values.
    effect(() => {
      const officer = this.officer();
      this.name = officer?.name ?? '';
      this.email = officer?.email ?? '';
      this.phone = officer?.phone ?? '';
    });
  }

  async updateProfile(): Promise<void> {
    this.busy.set(true);
    try {
      const result = await this.auth.updateProfile({ name: this.name, email: this.email, phone: this.phone });
      this.notify(result.message, result.ok);
    } finally {
      this.busy.set(false);
    }
  }

  async changePassword(): Promise<void> {
    if (!this.currentPassword) {
      this.currentPasswordError.set(true);
      this.notify('Enter your current password.', false);
      return;
    }
    if (!this.newPassword || this.newPassword.length < 4) {
      this.notify('New password must be at least 4 characters.', false);
      return;
    }
    if (this.newPassword === this.currentPassword) {
      this.notify('New password must be different from the current password.', false);
      return;
    }
    this.busy.set(true);
    try {
      const result = await this.auth.changePassword(this.currentPassword, this.newPassword);
      this.notify(result.message, result.ok);
      // Only a confirmed change clears the form; keeping the typed values on
      // failure is what tells the user the password was not accepted.
      this.currentPasswordError.set(!result.ok);
      if (result.ok) {
        this.currentPassword = '';
        this.newPassword = '';
      }
    } finally {
      this.busy.set(false);
    }
  }

  /** Re-triggers the toast animation even when the same text repeats. */
  private notify(text: string, ok: boolean): void {
    this.messageTone.set(ok ? 'success' : 'error');
    this.message.set(null);
    setTimeout(() => this.message.set(text));
  }

  logout(): void {
    this.auth.logout();
    void this.router.navigateByUrl('/');
  }
}
